import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { QUERY_KEYS } from '@/lib/constants';
import type { ApiResponse, SceneTree, SceneCard } from './types';

export function useSceneTree() {
  return useQuery({
    queryKey: QUERY_KEYS.scenes,
    queryFn: () => api.get<ApiResponse<SceneTree>>('/api/v1/scenes'),
    select: (res) => res.data,
  });
}

export function useSceneCard(id: string) {
  return useQuery({
    queryKey: ['scenes', 'cards', id],
    queryFn: () => api.get<ApiResponse<SceneCard>>(`/api/v1/scenes/cards/${id}`),
    select: (res) => res.data,
    enabled: !!id,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; sort_order?: number }) =>
      api.post('/api/v1/scenes/groups', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.scenes }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; sort_order?: number }) =>
      api.patch(`/api/v1/scenes/groups/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.scenes }),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/scenes/groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.scenes }),
  });
}

export function useCreateScene() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { group_id: string; name: string; description?: string }) =>
      api.post('/api/v1/scenes', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.scenes }),
  });
}

export function useCreateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { scene_id: string; name: string; tag_ids: string[]; difficulty?: number }) =>
      api.post('/api/v1/scenes/cards', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.scenes }),
  });
}

export function useDeleteCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/scenes/cards/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.scenes }),
  });
}
